import measuringGuide from "../../../content/technical-resources/field-measurement-workflow.md?raw";
import quoteChecklist from "../../../content/technical-resources/quote-package-checklist.md?raw";
import accessoryPlanning from "../../../content/technical-resources/accessory-planning-workflow.md?raw";

export type TechnicalResource = {
  slug: string;
  title: string;
  summary: string;
  resourceType: "Technical article" | "How-to guide" | "Planning checklist";
  schemaType: "TechArticle" | "HowTo";
  metaTitle: string;
  metaDescription: string;
  markdown: string;
};

const rawResources: Array<Omit<TechnicalResource, "markdown"> & { markdown: string }> = [
  {
    slug: "field-measurement-workflow",
    title: "Commercial Restroom Field Measurement Workflow",
    summary: "A planning workflow for collecting the dimensions, walls, floor conditions, fixture locations, and photos that make a commercial partition quote more useful.",
    resourceType: "How-to guide",
    schemaType: "HowTo",
    metaTitle: "Commercial Restroom Field Measurement Workflow | Partition Stalls",
    metaDescription: "Use this commercial restroom field-measurement workflow to organize partition dimensions, wall conditions, fixtures, photos, and planning questions before requesting a quote.",
    markdown: measuringGuide,
  },
  {
    slug: "quote-package-checklist",
    title: "Commercial Partition Quote Package Checklist",
    summary: "Prepare a Division 10 toilet partition bid or quote package with plans, specification references, quantities, finishes, and project-scope questions.",
    resourceType: "Planning checklist",
    schemaType: "HowTo",
    metaTitle: "Division 10 Toilet Partition Quote Checklist | Partition Stalls",
    metaDescription: "Prepare a Division 10 toilet partition quote package with CSI 10 21 13 references, plans, quantities, accessory schedules, and supply or installation scope.",
    markdown: quoteChecklist,
  },
  {
    slug: "accessory-planning-workflow",
    title: "Commercial Restroom Accessory Planning Workflow",
    summary: "A technical planning article for comparing commercial accessory models before final specifications, dimensions, mounting, finish, and compatibility are confirmed.",
    resourceType: "Technical article",
    schemaType: "TechArticle",
    metaTitle: "Commercial Restroom Accessory Planning Workflow | Partition Stalls",
    metaDescription: "Compare commercial restroom accessory model references while confirming mounting, dimensions, finish, capacity, and project requirements before a final quote.",
    markdown: accessoryPlanning,
  },
];

export const technicalResources: TechnicalResource[] = rawResources;

export function getTechnicalResource(slug: string | undefined) {
  return technicalResources.find((resource) => resource.slug === slug);
}

export type MarkdownSection = { heading: string; paragraphs: string[]; bullets: string[]; steps: string[] };

export function parseTechnicalMarkdown(markdown: string): MarkdownSection[] {
  const lines = markdown.split(/\r?\n/).map((line) => line.trim());
  const sections: MarkdownSection[] = [];
  let current: MarkdownSection = { heading: "Overview", paragraphs: [], bullets: [], steps: [] };

  const push = () => {
    if (current.heading || current.paragraphs.length || current.bullets.length || current.steps.length) sections.push(current);
  };

  for (const line of lines) {
    if (!line || line.startsWith("---")) continue;
    if (line.startsWith("# ")) continue;
    if (line.startsWith("## ")) {
      push();
      current = { heading: line.replace(/^##\s+/, ""), paragraphs: [], bullets: [], steps: [] };
      continue;
    }
    if (/^-\s+/.test(line)) {
      current.bullets.push(line.replace(/^-\s+/, ""));
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      current.steps.push(line.replace(/^\d+\.\s+/, ""));
      continue;
    }
    current.paragraphs.push(line);
  }
  push();
  return sections.filter((section) => section.paragraphs.length || section.bullets.length || section.steps.length);
}
